/**
 * THE AFFORDANCE CATALOG (workspace-client.md §3).
 *
 * The terminal's rule is that every command a player can type derives from one table (`SESSION_COMMANDS`),
 * so /help, Tab-completion and the chrome can never disagree about what exists. This is that rule
 * generalized per client: every button, gesture and composer action in the workspace derives from HERE.
 *
 * Two properties follow, and both are tested rather than asserted:
 *
 * 1. **Every affordance dispatches a canonical engine input.** No workspace-only verbs, ever - a
 *    capability that exists here must exist as a typed command in the terminal. `affordances.test.ts`
 *    reads the terminal's own dispatch table out of its source and checks every `kind` here appears
 *    there, so the rule cannot rot into a comment.
 * 2. **What is offered is what the engine would accept.** An affordance renders only when the
 *    projection says its precondition holds - the same discipline as "decide/vote/defer render only if
 *    the engine would accept them" (§2).
 */
import type { GameActionPayload } from '@core';
import { t } from './i18n.ts';
import type { MessageKey } from './i18n.ts';
import type { WorkspaceModel } from './model.ts';

/** Where an affordance appears. The medium's own grammar (§2m) decides, not a menu. */
export type Surface = 'member' | 'composer' | 'gesture' | 'room';

/** One thing a player can do, and what it costs them in ceremony (§2b). */
export interface Affordance {
  /** Stable id: the DOM hook and what the e2e specs point at. */
  id: string;
  surface: Surface;
  label: string;
  /** A second line, when the affordance needs to say what it will do. */
  hint: string | null;
  ceremony: 'immediate' | 'confirm' | 'confirm-with-note';
  /** The canonical input this dispatches. */
  action: GameActionPayload;
}

/** The verbs a role carries, in the words a colleague would use. Unmapped verbs keep their own name. */
const VERB_LABELS: Record<string, MessageKey> = {
  'declare-incident': 'verb.declare-incident',
  'talk-to-stakeholder': 'verb.talk-to-stakeholder',
  investigate: 'verb.investigate',
  mitigate: 'verb.mitigate',
  fix: 'verb.fix',
  'update-status': 'verb.update-status',
};

export function verbLabel(verb: string): string {
  const key = VERB_LABELS[verb];
  return key ? t(key) : t('verb.generic', { verb });
}

/**
 * Everything this player can do right now, derived from the projection.
 *
 * `ownerId` and `playerId` decide the one affordance that is not a seat's: starting the day belongs to
 * whoever opened the room (§2l), and offering it to everyone else would be offering a refusal.
 */
export function affordancesFor(model: WorkspaceModel, options: { isOwner: boolean }): Affordance[] {
  const list: Affordance[] = [];

  // Join-as-claim (§2): taking a role IS claiming a seat. Only while the room is gathering - during
  // play a bot-filled seat is a colleague, not an empty chair with a sign (§1b).
  //
  // DEC-16: this IS the "who are you?" picker, so it answers in the deck's own words. The hint is the
  // scenario's pitch for the role where one was authored, because "Does: mitigate, investigate" tells a
  // first-timer what the buttons are and not what the job is. The seat the scenario refuses to let a
  // bot hold is offered first, which is the deck recommending itself rather than the client guessing.
  const open = model.members.filter((m) => m.claimable && !model.you.seat);
  for (const member of [...open].sort((a, b) => Number(b.recommended) - Number(a.recommended))) {
    list.push({
      id: `claim:${member.role}`,
      surface: 'member',
      label: member.recommended ? t('member.takeRecommended') : t('member.take'),
      hint:
        member.pitch ??
        (member.does.length
          ? t('lobby.roleDoes', { powers: member.does.map(verbLabel).join(', ') })
          : null),
      ceremony: 'immediate',
      action: { kind: 'claimSeat', seat: member.role },
    });
  }

  // The day begins when the person who opened the room begins it (§2l).
  if (model.lobby && options.isOwner) {
    list.push({
      id: 'start',
      surface: 'room',
      label: t('lobby.start'),
      hint: t('lobby.startHint'),
      ceremony: 'immediate',
      action: { kind: 'startSession' },
    });
  }

  if (!model.you.seat) {
    return list;
  }

  // Your role's own verbs, as composer actions (§2 "Seat verbs"): comms sees "post a status update",
  // and the effect lands as a visible reply rather than as a number moving somewhere off screen.
  if (model.started) {
    for (const verb of model.you.verbs) {
      list.push({
        id: `verb:${verb}`,
        surface: 'composer',
        label: verbLabel(verb),
        hint: null,
        ceremony: 'immediate',
        action: { kind: 'seatAction', seat: model.you.seat, verb },
      });
    }
  }

  // THE CHECK-IN, wearing the only costume it ever needed (§2m): you end your day by signing off. The
  // mood two-tap rides the same gesture, so the director gets both signals for one action
  // (game-loop §9.2) and the player performs one ritual, not two chores.
  if (model.started && !model.you.signedOff) {
    list.push({
      id: 'signoff',
      surface: 'gesture',
      label: t('gesture.signOff'),
      hint: t('gesture.signOffHint'),
      ceremony: 'confirm',
      action: { kind: 'checkIn', seat: model.you.seat, mode: 'Passed' },
    });
  }

  return list;
}

/**
 * The five moods the engine actually accepts (`TeamSim.Engine.Moods.Mood`), keyed by the word this
 * client puts on the button.
 *
 * The two vocabularies are deliberately different and BOTH are right: the engine's names are what the
 * director reads and the retro plots, while "Good / Flat / Rough" is what a person taps at six o'clock
 * without thinking about it. What is not right is sending the button's word to the engine, which is
 * what the first build did - every mood pulse was refused with `invalid Mood: 'good'`, the sign-off
 * still went through, and nothing in the client or the tests noticed. Found by reading a screenshot.
 */
const MOOD_VALUES: Record<string, string> = {
  good: 'Happy',
  flat: 'Bored',
  rough: 'Stressed',
};

/** The mood half of the sign-off gesture, dispatched alongside the check-in (§2m). */
export function moodAction(seat: string, mood: string): GameActionPayload {
  return { kind: 'moodPulse', seat, mood: MOOD_VALUES[mood] ?? mood };
}

/** Exported for the test that keeps the button words and the engine's enum in step. */
export const MOOD_KEYS: readonly string[] = Object.keys(MOOD_VALUES);

/**
 * Resolving the incident, from a reply-button under the monitoring bot's post (§2n). The field names are
 * the terminal's, because it is the same input: `/resolve <optionId>` and this button are two spellings
 * of one canonical verb, not two verbs that happen to agree.
 */
export function resolveAction(eventId: string, optionId: string): GameActionPayload {
  return { kind: 'resolveEvent', eventId, optionId };
}

/**
 * The longest "why" the server will take (`GameInputCodec.MaxReasonLength`). Enforced here too, as a
 * `maxlength` on the field, so the limit is something the player runs into while typing rather than a
 * refusal after they commit - the same rule as everywhere else: tell them before, not after.
 */
export const MAX_REASON = 280;

/**
 * The inputs that carry a line of "why" (workspace-client.md §2b). Not every verb takes one: attaching a
 * reason to a check-in would be a field the codec ignores, and a client that sends fields nothing reads
 * is how a seam rots into decoration.
 */
const REASON_BEARING: readonly string[] = [
  'resolveEvent',
  'resolveDecision',
  'voteDecision',
  'approveDecision',
];

/**
 * Puts the player's line ON the input.
 *
 * <p>Before this, the confirm posted the line to the thread and nothing else - so the words lived in
 * chat, and the retro does not read chat. §2b's promise ("the retro quotes the player's own words back")
 * needs them committed next to the call they explain. The thread post STAYS: it is a message to
 * colleagues and always was. This adds the second, quieter destination.</p>
 */
export function withReason(input: GameActionPayload, reason: string): GameActionPayload {
  const line = reason.trim();
  if (!line || !REASON_BEARING.includes(input.kind)) {
    return input;
  }

  return { ...input, reason: line.slice(0, MAX_REASON) };
}

/** Exported for the test that keeps this list in step with the codec's own. */
export const REASON_BEARING_KINDS = REASON_BEARING;

/**
 * What this player may do about one open decision, right now (§2b, §2q).
 *
 * <p>The rule this implements is §2's: <b>an affordance renders only when the engine would accept
 * it.</b> The rights styles are not decoration - `DecisionEngine` refuses a non-owner's resolve, a
 * committee resolve without quorum, an approval-chain resolve before sign-off - and a button that
 * produces a refusal teaches a player that the interface lies. So the style decides the buttons.</p>
 *
 * <p>Every one of these is Gate-tier, so every one carries §2b's required line of why. That line now
 * rides the input itself (`reason`), which is what lane 10a item 5 built and what the retro quotes
 * back verbatim - the confirm is where the two halves meet.</p>
 */
export function decisionActions(decision: DecisionCard, seat: string | null): Affordance[] {
  if (!seat) {
    // No seat, no rights. Watching a call happen is a real state (DEC-16's takeover case) and it is
    // not the same as being able to make it.
    return [];
  }

  const list: Affordance[] = [];
  const owner = decision.owner === seat;

  // VOTE - the objection surface. Open to everyone in a committee, and open to everyone under
  // OwnerDecides too, where §7.4 prices an override rather than blocking it: recording that you
  // disagreed is exactly how that price gets paid.
  if (decision.style === 'Committee' || decision.style === 'OwnerDecides') {
    for (const option of decision.options) {
      list.push({
        id: `vote:${decision.id}:${option}`,
        surface: 'room',
        label: t('decision.vote', { option }),
        hint: null,
        ceremony: 'confirm-with-note',
        action: { kind: 'voteDecision', decision: decision.id, seat, option },
      });
    }
  }

  // APPROVE - only the seat the call actually requires, and only while it is still waiting.
  if (decision.style === 'ApprovalChain' && !decision.approved && decision.approver === seat) {
    list.push({
      id: `approve:${decision.id}`,
      surface: 'room',
      label: t('decision.approve'),
      hint: null,
      ceremony: 'confirm-with-note',
      action: { kind: 'approveDecision', decision: decision.id, seat },
    });
  }

  // DECIDE - the close. Offered only where the engine would take it: the owner, and only once the
  // style's precondition is met. A "decide" button on a call awaiting two more consults is a refusal
  // with a nice label on it.
  const canClose =
    owner &&
    (decision.style === 'OwnerDecides' ||
      (decision.style === 'ConsultThenDecide' && decision.consulted >= decision.requiredConsults) ||
      (decision.style === 'ApprovalChain' && decision.approved) ||
      (decision.style === 'Committee' && decision.votes >= decision.quorum));

  if (canClose) {
    for (const option of decision.options) {
      list.push({
        id: `decide:${decision.id}:${option}`,
        surface: 'room',
        label: t('decision.decide', { option }),
        hint: null,
        ceremony: 'confirm-with-note',
        action: { kind: 'resolveDecision', decision: decision.id, option, bySeat: seat },
      });
    }
  }

  // DEFER - always available and always recorded (§6.5: an explicit move, not an absence). It carries
  // no note: putting something off is not an argument, and the retro counts it rather than quoting it.
  list.push({
    id: `defer:${decision.id}`,
    surface: 'room',
    label: t('decision.defer'),
    hint: decision.deferrals > 0 ? t('decision.deferredBefore', { n: decision.deferrals }) : null,
    ceremony: 'confirm',
    action: { kind: 'deferDecision', decision: decision.id },
  });

  return list;
}

/** The shape `decisionActions` reads - the projection's decision, narrowed to what rights need. */
export interface DecisionCard {
  id: string;
  style: string;
  owner: string;
  options: string[];
  consulted: number;
  requiredConsults: number;
  votes: number;
  quorum: number;
  approved: boolean;
  approver: string | null;
  deferrals: number;
}

/**
 * Inputs this client sends ON ITS OWN BEHALF, not because a player asked for anything.
 *
 * The "no workspace-only verbs" rule exists to stop this client growing CAPABILITIES the terminal does
 * not have - a player able to do something here and nowhere else. A read-marker is not a capability:
 * nobody chooses it, no affordance produces it, and there is nothing for a terminal player to type
 * because the terminal marks nothing. So the rule takes a carve-out, and the carve-out is itself closed
 * and tested - a list of two is a documented exception, a list of twenty is the rule quietly repealed.
 */
export const AUTOMATIC: readonly string[] = ['markMessageSeen'];

/** Exported for the "no workspace-only verbs" test: every input kind this client can ever send. */
export const DISPATCHED_KINDS: readonly string[] = [
  'claimSeat',
  'startSession',
  'seatAction',
  'checkIn',
  'moodPulse',
  'resolveEvent',
  'advanceBeat',
  'voteDecision',
  'approveDecision',
  'resolveDecision',
  'deferDecision',
  ...AUTOMATIC,
];
